import React, { ReactNode, useContext, useMemo } from "react";
import { Plus } from "lucide-react";
import { useSelector } from "react-redux";
import { FilterType, LanguageNameSpaces, PrecisionType } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useClientsData } from "@shared/utils/hooks/misc";
import { portfolioFilters } from "@shared/slices/filters.slice";
import {
  selectPortfolioDataLoadingStates,
  selectPortfolioPrecision,
  selectUniqueAccountsPortfolioData,
} from "@shared/slices/portfolio.slice";
import { usePortfolioPieChart } from "@shared/features/portfolio/hooks";
import { PORTFOLIO_DATA_CLIENT_HEADER } from "@shared/features/portfolio/constants";
import Button from "common/Buttons/Button";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { Panel, SectionHeader, usePageIntro } from "common/NewLook";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import PortfolioLineChart from "features/portfolio/PortfolioLineChart";
import AccountsSortSelect from "features/portfolio/Accounts/AccountsSortSelect.tsx";
import { AccountsPageContext } from "features/portfolio/Accounts/AccountsPage.context.ts";
import AllocationCard from "features/portfolio/NewLook/AllocationCard.tsx";
import NewLookFiltersBar from "features/portfolio/NewLook/NewLookFiltersBar.tsx";
import NewLookLayoutSwitch from "features/portfolio/NewLook/NewLookLayoutSwitch.tsx";
import NewLookOverview from "features/portfolio/NewLook/NewLookOverview.tsx";
import useLatestRef from "features/portfolio/NewLook/useLatestRef.ts";
import {
  NewLookPageBody,
  NewLookPanelTable,
  NewLookToolbarGroup,
} from "features/portfolio/NewLook/styles.ts";

interface NewLookAccountsProps {
  /** Undefined when the user can't create accounts (sub users). */
  onCreateClick?: (event?: React.MouseEvent) => void;
  searchInput?: ReactNode;
  mainContent: ReactNode;
  hideFilterTypes: FilterType[];
}

/**
 * New-look accounts page: filters and the create action share the toolbar
 * under the top bar, then one overview panel (allocation + balance history)
 * and the accounts as cards or a table.
 */
const NewLookAccounts = ({
  onCreateClick,
  searchInput,
  mainContent,
  hideFilterTypes,
}: NewLookAccountsProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { accounts, isCardsView, setIsCardsView, isLoading } =
    useContext(AccountsPageContext)!;
  const onCreateRef = useLatestRef(onCreateClick);
  const { notDeletedClientsData } = useClientsData();
  // With no accounts at all the empty state's button is the one call to
  // action; the toolbar doesn't repeat it.
  const canCreate =
    !!onCreateClick &&
    (notDeletedClientsData === undefined || notDeletedClientsData.length > 0);
  const { actions, selectors } = portfolioFilters;

  usePageIntro(
    {
      footer: (
        <NewLookFiltersBar
          filtersActions={actions}
          filtersSelectors={selectors}
          hideFilterTypes={hideFilterTypes}
        />
      ),
      actions: canCreate ? (
        <Button
          type="primary"
          onClick={(event: React.MouseEvent) => onCreateRef.current?.(event)}
        >
          <Plus size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
          {t("buttons.create")}
        </Button>
      ) : undefined,
    },
    [canCreate, hideFilterTypes, i18n.language]
  );

  const hasAccounts = isLoading || accounts.length > 0;
  const count = isLoading ? undefined : accounts.length;
  const listActions = (
    <NewLookToolbarGroup>
      {searchInput}
      <AccountsSortSelect />
      <NewLookLayoutSwitch
        isCardsView={isCardsView}
        onClick={() => setIsCardsView(!isCardsView)}
      />
    </NewLookToolbarGroup>
  );

  const renderList = () => {
    if (isCardsView)
      return (
        <section>
          {hasAccounts && (
            <SectionHeader
              title={t("tabs.accounts")}
              count={count}
              actions={listActions}
            />
          )}
          {mainContent}
        </section>
      );
    return (
      <Panel
        title={hasAccounts ? t("tabs.accounts") : undefined}
        count={hasAccounts ? count : undefined}
        actions={hasAccounts ? listActions : undefined}
        flush={true}
      >
        <NewLookPanelTable>{mainContent}</NewLookPanelTable>
      </Panel>
    );
  };

  return (
    <NewLookPageBody>
      {/* With no accounts the charts are empty; let the empty state lead. */}
      {hasAccounts && (
        <NewLookOverview
          allocationTitle={t("charts.accounts_allocation")}
          allocation={<AccountsAllocation bare={true} />}
          chart={<PortfolioLineChart />}
        />
      )}
      {renderList()}
    </NewLookPageBody>
  );
};

export default NewLookAccounts;

interface AccountsAllocationProps {
  /** Without the card and title, for the overview panel. */
  bare?: boolean;
  /** Smaller donut, for phones. */
  compact?: boolean;
}

export const AccountsAllocation = React.memo(
  ({ bare, compact }: AccountsAllocationProps) => {
    const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
    const { loading } = useSelector(selectPortfolioDataLoadingStates);
    const { pieChartData, total } = usePortfolioPieChart({
      dataSelector: selectUniqueAccountsPortfolioData,
      nameHeader: PORTFOLIO_DATA_CLIENT_HEADER,
    });
    const totalText = useMemo(
      () => (
        <TextWithPrecisionAndIcon
          precisionSelector={selectPortfolioPrecision}
          precisionType={PrecisionType.AMOUNT}
          text={total}
          round={true}
        />
      ),
      [total]
    );
    return (
      <AllocationCard
        title={bare ? undefined : t("charts.accounts_allocation")}
        data={pieChartData}
        total={totalText}
        loading={loading}
        chartId="accounts-chart"
        bare={bare}
        compact={compact}
      />
    );
  }
);
